import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import AdminDashboard from './pages/AdminDashboard';
import DriveCallback from './pages/DriveCallback';
import OTPVerifyPage from './pages/OTPVerifyPage';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// Configure axios globally for Safari/iOS compatibility
const TOKEN_KEY = 'expenseflow_session_token';

axios.interceptors.request.use(
  (config) => {
    // Always include credentials
    config.withCredentials = true;
    
    // Add Authorization header from localStorage if available
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // On 401, clear token (except for login/OTP pages)
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/verify-otp' && path !== '/') {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    return Promise.reject(error);
  }
);

// Dark Mode Context
export const DarkModeContext = createContext();

export const useDarkMode = () => useContext(DarkModeContext);

function AppRouter() {
  const location = useLocation();
  
  // Check for session_id in hash - use both React Router and window location
  // Safari/iOS may have timing issues with React Router's location.hash
  const hasSessionId = location.hash?.includes('session_id=') || 
                       window.location.hash?.includes('session_id=');
  
  if (hasSessionId) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-otp" element={<OTPVerifyPage />} />
      <Route path="/drive-callback" element={<DriveCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleDarkMode = (value) => {
    setDarkMode(typeof value === 'boolean' ? value : !darkMode);
  };

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <div className={`App ${darkMode ? 'dark' : ''}`}>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors theme={darkMode ? 'dark' : 'light'} />
      </div>
    </DarkModeContext.Provider>
  );
}

export default App;